/**
 * ChangeHistory Component
 * Shows change history for a record in master data forms
 * Displays last modified info with field changes (what changed from what to what)
 */

import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Stack,
} from '@mui/material';
import { History as HistoryIcon, ArrowForward as ArrowIcon } from '@mui/icons-material';

interface FieldChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

interface LastChangeData {
  lastModifiedBy: string;
  lastModifiedAt: string;      // Formatted date from server
  lastModifiedAgo: string;     // Relative time from server (e.g., "2 hours ago")
  operation: string;
  changedFields?: string[] | null;
  changedFieldsCount?: number;
  fieldChanges?: FieldChange[];
  schemaName?: string;
  tenantId?: number;
  tenantName?: string;
  tenantCode?: string;
}

interface ChangeHistoryProps {
  lastChange?: LastChangeData | null;
}

const operationColors: Record<string, 'success' | 'info' | 'error' | 'default'> = {
  INSERT: 'success',
  UPDATE: 'info',
  DELETE: 'error',
};

const operationLabels: Record<string, string> = {
  INSERT: 'Created',
  UPDATE: 'Last updated',
  DELETE: 'Deleted',
  RESTORE: 'Restored',
};

export default function ChangeHistory({ lastChange }: ChangeHistoryProps) {
  if (!lastChange || !lastChange.lastModifiedAt) {
    return null;
  }

  const fieldChanges = lastChange.fieldChanges || [];
  const hasChanges = fieldChanges.length > 0;

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          {operationLabels[lastChange.operation] || lastChange.operation} by{' '}
          <Typography component="span" variant="caption" fontWeight="medium" color="text.primary">
            {lastChange.lastModifiedBy || 'Unknown'}
          </Typography>
          {' '}
          <Tooltip title={lastChange.lastModifiedAt}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
              {lastChange.lastModifiedAgo || lastChange.lastModifiedAt}
            </Typography>
          </Tooltip>
          {lastChange.tenantName && (
            <>
              {' in '}
              <Typography component="span" variant="caption" fontWeight="medium" color="primary.main">
                {lastChange.tenantName}
              </Typography>
            </>
          )}
        </Typography>
        <Chip
          size="small"
          label={operationLabels[lastChange.operation] || lastChange.operation}
          color={operationColors[lastChange.operation] || 'default'}
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      </Stack>

      {/* Show field changes with old and new values */}
      {hasChanges && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Changes:
          </Typography>
          <Stack spacing={0.5}>
            {fieldChanges.slice(0, 5).map((change) => (
              <Box
                key={change.field}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  pl: 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="medium"
                  sx={{ minWidth: 80, color: 'text.primary' }}
                >
                  {change.label}:
                </Typography>
                <Chip
                  size="small"
                  label={change.oldValue || '-'}
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: 'error.50',
                    borderColor: 'error.200',
                    textDecoration: 'line-through',
                    opacity: 0.8,
                  }}
                />
                <ArrowIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Chip
                  size="small"
                  label={change.newValue || '-'}
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: 'success.50',
                    borderColor: 'success.200',
                  }}
                />
              </Box>
            ))}
            {fieldChanges.length > 5 && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                +{fieldChanges.length - 5} more changes
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
